import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router';
import i18n from 'i18next';
import { useTranslation } from 'react-i18next';
import { useAlert } from 'contexts/AlertContext';
import { MeetingRepository } from 'repository/meetingRepository';
import { meetingStore } from 'store/meetingStore';
import { useMeetingList } from 'hooks/useMeeting';
import {
    AppScreen, Button, ButtonFooter, PageIntro, Section, SectionHeader,
} from 'components/ds';
import styles from './SharedPlace.module.css';

interface SubPlace {
    subId: number;
    category: string;
    placeName?: string;
    placeAddress?: string;
}

interface PlaceOption {
    key: string;
    label: string;
    type: 'main' | 'sub-update' | 'sub-create';
    subId?: number;       // sub-update 시 기존 subId
    category: string;     // PRIMARY, SECONDARY, TERTIARY
    currentPlace?: string; // 현재 설정된 장소명 (수정 시 표시용)
}

// 일반 모임은 슬롯형 (1차/2차/3차 — 같은 카테고리 1개씩만), 여행 모임은 자유 추가형 (관광지/맛집/카페/숙소 N개)
const CATEGORY_ORDER_DAY = ['PRIMARY', 'SECONDARY', 'TERTIARY'];
const CATEGORY_ORDER_TRAVEL = ['ATTRACTION', 'RESTAURANT', 'CAFE', 'HOTEL'];
const CATEGORY_LABELS: Record<string, string> = {
    PRIMARY: '1차',
    SECONDARY: '2차',
    TERTIARY: '3차',
    ATTRACTION: i18n.t('category.attraction'),
    RESTAURANT: i18n.t('category.restaurant'),
    CAFE: i18n.t('category.cafe'),
    HOTEL: i18n.t('category.hotel'),
};

export const SharedPlace = () => {
    const navigate = useNavigate();
    const { t } = useTranslation();
    const [searchParams] = useSearchParams();
    const { showAlert } = useAlert();
    const { data, status } = useMeetingList();

    const [selectedMeetingId, setSelectedMeetingId] = useState<string | null>(null);
    const [selectedOption, setSelectedOption] = useState<string | null>(null);
    const [placeOptions, setPlaceOptions] = useState<PlaceOption[]>([]);
    const [isUpdating, setIsUpdating] = useState(false);
    const [loadingSubPlaces, setLoadingSubPlaces] = useState(false);

    const placeInfo = {
        name: searchParams.get('name') || '공유된 장소',
        address: searchParams.get('address') || '',
        lat: parseFloat(searchParams.get('lat') || '0'),
        lng: parseFloat(searchParams.get('lng') || '0'),
    };

    useEffect(() => {
        meetingStore.onGetMeetingList();
    }, []);

    const meetings = data?.data || [];
    const isLoading = status === 'pending' || status === 'default';

    // 모임 선택 시 기존 서브 장소 조회 → 옵션 동적 생성
    const handleMeetingSelect = async (meetingId: string) => {
        if (selectedMeetingId === meetingId) {
            setSelectedMeetingId(null);
            setSelectedOption(null);
            setPlaceOptions([]);
            return;
        }

        setSelectedMeetingId(meetingId);
        setSelectedOption(null);
        setLoadingSubPlaces(true);

        const meeting = meetings.find((m: any) => m.meetingId === meetingId);
        const isTravelMeeting = meeting?.meetingType === 'TRAVEL';
        const categoryOrder = isTravelMeeting ? CATEGORY_ORDER_TRAVEL : CATEGORY_ORDER_DAY;
        const options: PlaceOption[] = [];

        // 메인 장소(집결지) 옵션 — 여행 모임도 집결지가 있음
        options.push({
            key: 'main',
            label: meeting?.place?.placeName ? (isTravelMeeting ? t('sharedPlace.changeGather') : t('sharedPlace.changeMain')) : (isTravelMeeting ? t('sharedPlace.setGather') : t('sharedPlace.setMain')),
            type: 'main',
            category: 'MAIN',
            currentPlace: meeting?.place?.placeName || undefined,
        });

        // 서브 장소 옵션: 기존 것 조회
        try {
            const res = await MeetingRepository.onGetSubPlaces(meetingId);
            const subPlaces: SubPlace[] = res?.data?.data || res?.data || [];

            if (isTravelMeeting) {
                // 여행 모임 — 카테고리별 추가 (HOTEL은 다중 가능, 나머지도 자유 추가)
                for (const cat of categoryOrder) {
                    options.push({
                        key: `sub-${cat}`,
                        label: t('sharedPlace.addAsCat', { cat: CATEGORY_LABELS[cat] }),
                        type: 'sub-create',
                        category: cat,
                    });
                }
            } else {
                // 일반 모임 — 슬롯 모델 (PRIMARY/SECONDARY/TERTIARY)
                const existingByCategory: Record<string, SubPlace> = {};
                for (const sp of subPlaces) {
                    if (categoryOrder.includes(sp.category)) {
                        existingByCategory[sp.category] = sp;
                    }
                }
                for (const cat of categoryOrder) {
                    const existing = existingByCategory[cat];
                    if (existing) {
                        options.push({
                            key: `sub-${cat}`,
                            label: t('sharedPlace.changeCatPlace', { cat: CATEGORY_LABELS[cat] }),
                            type: 'sub-update',
                            subId: existing.subId,
                            category: cat,
                            currentPlace: existing.placeName,
                        });
                    } else {
                        options.push({
                            key: `sub-${cat}`,
                            label: t('sharedPlace.addCatPlace', { cat: CATEGORY_LABELS[cat] }),
                            type: 'sub-create',
                            category: cat,
                        });
                    }
                }
            }
        } catch {
            // 서브 장소 조회 실패 시 기본 옵션
            for (const cat of categoryOrder) {
                options.push({
                    key: `sub-${cat}`,
                    label: t('sharedPlace.addCatPlace', { cat: CATEGORY_LABELS[cat] }),
                    type: 'sub-create',
                    category: cat,
                });
            }
        } finally {
            setLoadingSubPlaces(false);
        }

        setPlaceOptions(options);
    };

    // 확인
    const handleConfirm = async () => {
        if (!selectedMeetingId || !selectedOption) return;
        const option = placeOptions.find(o => o.key === selectedOption);
        if (!option) return;

        setIsUpdating(true);
        try {
            if (option.type === 'main') {
                await MeetingRepository.onPutUpdateMeeting(selectedMeetingId, {
                    placeName: placeInfo.name,
                    placeAddress: placeInfo.address,
                    latitude: placeInfo.lat,
                    longitude: placeInfo.lng,
                });
            } else if (option.type === 'sub-update' && option.subId) {
                await MeetingRepository.onPutUpdateSubPlace(selectedMeetingId, option.subId, {
                    placeName: placeInfo.name,
                    placeAddress: placeInfo.address,
                    latitude: placeInfo.lat,
                    longitude: placeInfo.lng,
                    category: option.category,
                });
            } else {
                await MeetingRepository.onPostCreateSubPlace(selectedMeetingId, {
                    placeName: placeInfo.name,
                    placeAddress: placeInfo.address,
                    latitude: placeInfo.lat,
                    longitude: placeInfo.lng,
                    category: option.category,
                });
            }

            const actionLabel = option.type === 'sub-update' || (option.type === 'main' && option.currentPlace)
                ? t('sharedPlace.actionChange') : t('sharedPlace.actionSet');
            await showAlert({ message: t('sharedPlace.placeSetMsg', { action: actionLabel }) });
            // 외부 deeplink 진입 시 history stack 의 잔여 entry (NotFound 등) 정리:
            // 현재 SharedPlace entry 를 home 으로 replace 한 후 detail 을 push.
            // → 뒤로가기: detail → home → 빈 stack (앱 종료 팝업).
            try { window.history.replaceState({}, '', '/home'); } catch { /* 무시 */ }
            navigate(`/meeting/detail/${selectedMeetingId}`);
        } catch {
            await showAlert({ message: t('sharedPlace.placeSetFail') });
        } finally {
            setIsUpdating(false);
        }
    };

    const handleCreateNew = () => {
        // 모임 생성 흐름은 sessionStorage 의 'meeting_create_data' 에 모든 단계의 입력을 저장.
        // location.state 만으로는 step 사이 navigate 시 사라져 PlaceExplore 같은 중간 단계에서
        // sharedPlace 정보가 유실됨 → sessionStorage 에 미리 셋해두면 Step2 / PlaceExplore 모두
        // savedData.selectedPlace / selectedPlaceInfo 로 자동 복원.
        try {
            const existing = JSON.parse(sessionStorage.getItem('meeting_create_data') || '{}');
            sessionStorage.setItem('meeting_create_data', JSON.stringify({
                ...existing,
                selectedPlace: placeInfo.name,
                selectedPlaceInfo: {
                    name: placeInfo.name,
                    address: placeInfo.address,
                    lat: placeInfo.lat,
                    lng: placeInfo.lng,
                },
            }));
        } catch { /* storage full 등 무시 */ }
        // 외부 deeplink stack 정리 — 현재 SharedPlace entry 를 home 으로 replace 후 create push.
        try { window.history.replaceState({}, '', '/home'); } catch { /* 무시 */ }
        navigate('/meeting/create', {
            state: { sharedPlace: placeInfo },
        });
    };

    const selectedOptionData = placeOptions.find(o => o.key === selectedOption);
    const buttonText = isUpdating
        ? t('sharedPlace.setting')
        : selectedOptionData
            ? selectedOptionData.label
            : t('sharedPlace.selectMeetingType');

    return (
        <AppScreen title={t('sharedPlace.title')} bar="sub" hasFooter>
            <PageIntro title={placeInfo.name} desc={placeInfo.address || undefined} />

            {/* 새로 만들기 */}
            <Section spacing="tight">
                <Button variant="secondary" size="lg" block onClick={handleCreateNew}>
                    {t('sharedPlace.newMeeting')}
                </Button>
            </Section>

            {/* 이미 있는 모임에 넣기 */}
            <Section divided last>
                <SectionHeader title={t('sharedPlace.addToExisting')} count={meetings.length} />

                {isLoading ? (
                    <p className={styles.state}>{t('sharedPlace.loadingMeetings')}</p>
                ) : meetings.length === 0 ? (
                    <p className={styles.state}>{t('sharedPlace.noMeetings')}</p>
                ) : (
                    <ul className={styles.list}>
                        {meetings.map((meeting: any) => {
                            const open = selectedMeetingId === meeting.meetingId;
                            return (
                                <li key={meeting.meetingId}>
                                    <button
                                        type="button"
                                        className={open ? `${styles.row} ${styles.rowOn}` : styles.row}
                                        onClick={() => handleMeetingSelect(meeting.meetingId)}
                                        aria-expanded={open}
                                    >
                                        <span className={styles.rowBody}>
                                            <span className={styles.rowTitle}>{meeting.meetingName}</span>
                                            <span className={styles.rowMeta}>
                                                {meeting.place?.placeName || t('meeting.placeNotSet')}
                                            </span>
                                        </span>
                                    </button>

                                    {open && (
                                        <div className={styles.options}>
                                            {loadingSubPlaces ? (
                                                <p className={styles.state}>{t('sharedPlace.checkingPlace')}</p>
                                            ) : (
                                                placeOptions.map((option) => (
                                                    <button
                                                        key={option.key}
                                                        type="button"
                                                        className={selectedOption === option.key
                                                            ? `${styles.pick} ${styles.pickOn}`
                                                            : styles.pick}
                                                        onClick={() => setSelectedOption(option.key)}
                                                        aria-pressed={selectedOption === option.key}
                                                    >
                                                        <span className={styles.pickMark} aria-hidden />
                                                        <span className={styles.pickBody}>
                                                            <span className={styles.pickTitle}>{option.label}</span>
                                                            {option.currentPlace && (
                                                                <span className={styles.pickSub}>
                                                                    {t('sharedPlace.current', { place: option.currentPlace })}
                                                                </span>
                                                            )}
                                                        </span>
                                                    </button>
                                                ))
                                            )}
                                        </div>
                                    )}
                                </li>
                            );
                        })}
                    </ul>
                )}
            </Section>

            <ButtonFooter>
                <Button
                    variant="primary"
                    size="lg"
                    block
                    onClick={handleConfirm}
                    disabled={!selectedMeetingId || !selectedOption || isUpdating}
                >
                    {buttonText}
                </Button>
            </ButtonFooter>
        </AppScreen>
    );
};
